/**
 * "Sign in to your DoktorABC account" modal for Variant B.
 * Shown when a returning patient answers the entry gate, or via the header
 * sign-in link. Layout matches Weight-Loss Figma (node 5313:115793 / 115801):
 * Google → email divider → labeled fields → forgot password → orange Login →
 * orange outline Skip → register footer.
 */

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as translator from "../../../utils/translator";
import * as http from "../../../utils/http";
import VariantBModalShell from "./VariantBModalShell";
import { useShellIdFn } from "./shellContext";
import SocialLoginButtons from "../../../components/LoginStrategies/SocialLoginButtons";
import InputField from "../../../components/CredentialsInput/InputField";
import ForgotPasswordModal from "../../../components/Modals/ForgotPasswordModal/ForgotPasswordModal";
import StickyNotification from "../../../components/StickyNotification/StickyNotification";
import { useCustomer } from "../../../customHooks/useCustomer";
import useForm from "../../../customHooks/useForm";
import { clientConfig } from "../../../config";
import ReCAPTCHA from "react-google-recaptcha";

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSkip: () => void;
  onLoginSuccess: () => void;
  /** Error raised outside this modal (e.g. a failed Google sign-up completion) to show on open. */
  externalError?: string | null;
  language: string;
  country: string;
}

const ORANGE_GRADIENT = "linear-gradient(90deg, #f59200 0%, #ffbb29 100%)";
const ORANGE = "#f08e00";

const SignInModal: React.FC<SignInModalProps> = ({
  isOpen,
  onClose,
  onSkip,
  onLoginSuccess,
  externalError,
  language,
  country,
}) => {
  const { customerState, saveCustomer } = useCustomer();
  const scopeId = useShellIdFn();
  const reRef = useRef<ReCAPTCHA>(null);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [stickyNotificationDetails, setStickyNotificationDetails] = useState({
    show: false,
    title: "",
    content: "",
    level: "",
    showInfoIcon: true,
    refresh: false,
  });

  const loginFormAttributes = useForm(
    { email: "", loginPassword: "", country },
    language,
    undefined,
    { looseEmail: true }
  );

  // Detect a successful login (email/password or OAuth) while this modal is open.
  useEffect(() => {
    if (isOpen && customerState?.customerID) {
      onLoginSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerState?.customerID, isOpen]);

  // Reset ephemeral UI whenever the popup opens/closes.
  useEffect(() => {
    setShowForgotPasswordModal(false);
    setSubmitLoading(false);
  }, [isOpen]);

  // Surface an error the host raised (e.g. Google sign-up completion failed) once open.
  useEffect(() => {
    if (isOpen && externalError) showError(externalError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, externalError]);

  // Keep OAuth returning to this same questionnaire page; "questionnaire" in the
  // path drives SocialLoginButtons' loginSource tagging.
  const currentPath = `${window.location.pathname.replace(/^\//, "")}${window.location.search}`;

  const showError = (content: string) => {
    setStickyNotificationDetails((current) => ({
      show: true,
      title: translator.translate(language, "default", "StickyNotification/danger_title"),
      content,
      level: "error",
      showInfoIcon: true,
      refresh: !current.refresh,
    }));
  };

  const onSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    setSubmitLoading(true);

    if (!loginFormAttributes.validateForm()) {
      setSubmitLoading(false);
      return;
    }

    let captchaToken: string | null | undefined;
    if (clientConfig.IS_RECAPTCHA_ENABLED) {
      captchaToken = await reRef.current?.executeAsync();
      reRef.current?.reset();
    }

    const requestBody = { ...loginFormAttributes.form, captchaToken };

    try {
      const response = await http.postToServer(`auth/local/login`, requestBody, [
        http.MIDDLEWARES.CART_ORDER,
        http.MIDDLEWARES.ENCRYPTED_BODY,
      ]);

      if (response.data.status === "ok") {
        saveCustomer({
          customerID: response.data.customerID,
          uuid: response.data.uuid,
          token: response.data.token,
          email: loginFormAttributes.form.email,
          optForSendables: response.data.optForSendables,
          fullName: response.data.customerFullName,
          loginSource: "questionnaire",
          provider: response.data.provider || "local",
          gender: response.data?.gender,
          lastPaidCategoryMention: response.data?.lastPaidCategoryMention,
        });
      } else {
        showError(translator.translate(language, "default", "StickyNotification/login_failed"));
      }
    } catch (error: any) {
      const content =
        error?.response?.data?.status === "user_blocked"
          ? translator.translate(language, "default", "StickyNotification/user_blocked")
          : translator.translate(language, "default", "StickyNotification/login_failed");
      showError(content);
    } finally {
      setSubmitLoading(false);
    }
  };

  const emailLabel = translator.translate(language, "default", "QuestionnairePage/signin_email_label");
  const passwordLabel = translator.translate(language, "default", "QuestionnairePage/signin_password_label");

  return (
    // Lighter dimmer than the default PortaledModal navy so the underlying
    // questionnaire card stays readable behind the sign-in popup (Figma).
    <VariantBModalShell
      modalId="wl2_signin_modal"
      isOpen={isOpen}
      onClose={onClose}
      maxWidthPx={366}
      mobileFullWidth
      // Failed login must keep the modal open — only X / Skip / success dismiss it.
      shouldBackgroundClickHideModal={false}
      backgroundColor="rgba(8, 8, 10, 0.35)"
    >
      <StickyNotification
        showNotification={stickyNotificationDetails.show}
        title={stickyNotificationDetails.title}
        onClick={() => setStickyNotificationDetails((current) => ({ ...current, show: false }))}
        content={stickyNotificationDetails.content}
        level={stickyNotificationDetails.level}
        showInfoIcon={stickyNotificationDetails.showInfoIcon}
        refresh={stickyNotificationDetails.refresh}
      />

      {showForgotPasswordModal && (
        <ForgotPasswordModal
          closeModal={() => setShowForgotPasswordModal(false)}
          language={language}
          setStickyNotificationDetails={setStickyNotificationDetails}
        />
      )}

      {/* Tighter spacing on short screens so the pop-up scrolls less. */}
      <div className="flex flex-col items-center gap-8 [@media(max-height:760px)]:gap-5 w-full pt-1">
        <h2 className="font-['Poppins'] font-medium text-[24px] [@media(max-height:760px)]:text-[20px] leading-[1.3] text-black text-center m-0 px-2 max-w-[278px]">
          {translator.translate(language, "default", "QuestionnairePage/signin_modal_title")}
        </h2>

        <div className="flex flex-col items-stretch gap-6 [@media(max-height:760px)]:gap-4 w-full [@media(max-height:760px)]:[&_#social-login-buttons_button]:py-[14px]">
          <SocialLoginButtons
            googleButtonId={scopeId("wl2_signin_google")}
            redirectPath={currentPath}
            cameFromPath={currentPath}
            language={language}
            country={country}
            onFailureSignup={() => {
              showError(translator.translate(language, "default", "StickyNotification/oauth_failed"));
            }}
          />

          <div className="flex flex-row items-center justify-center gap-[5px] w-full">
            <div className="h-px bg-[#E5E5E5] flex-1" />
            <span className="font-['Poppins'] font-normal text-[12px] leading-6 tracking-[-0.132px] text-[#ADADAD] uppercase whitespace-nowrap">
              {translator.translate(language, "authentication", "LoginPage/or_continue_with_email")}
            </span>
            <div className="h-px bg-[#E5E5E5] flex-1" />
          </div>

          <div className="flex flex-col items-center gap-4 [@media(max-height:760px)]:gap-3 w-full">
            <form onSubmit={onSubmit} className="flex flex-col gap-3 w-full" id="variant-b-signin-form">
              <InputField
                id={scopeId("wl2_signin_email")}
                value={loginFormAttributes.form.email}
                errorMsg={loginFormAttributes.errorMessagePerField.email}
                onChange={(value: string) => loginFormAttributes.updateForm("email", value)}
                type="email"
                label={emailLabel}
                placeholder={emailLabel}
                language={language}
                mandatory
                inputClassName="!h-[60px] [@media(max-height:760px)]:!h-12"
                onBlur={() => loginFormAttributes.onFieldBlur("email")}
                touched={loginFormAttributes.touched.email}
              />

              <InputField
                id={scopeId("wl2_signin_password")}
                value={loginFormAttributes.form.loginPassword}
                errorMsg={loginFormAttributes.errorMessagePerField.loginPassword}
                onChange={(value: string) => loginFormAttributes.updateForm("loginPassword", value)}
                type="password"
                label={passwordLabel}
                placeholder={passwordLabel}
                language={language}
                mandatory
                inputClassName="!h-[60px] [@media(max-height:760px)]:!h-12"
                onBlur={() => loginFormAttributes.onFieldBlur("loginPassword")}
                touched={loginFormAttributes.touched.loginPassword}
              />

              <input type="submit" hidden />

              {clientConfig.IS_RECAPTCHA_ENABLED && (
                <ReCAPTCHA sitekey={clientConfig.RECAPTCHA_SITE_KEY} size="invisible" ref={reRef} />
              )}
            </form>

            <button
              id={scopeId("wl2_signin_forgot_password")}
              type="button"
              onClick={() => setShowForgotPasswordModal(true)}
              className="font-['Poppins'] font-normal text-[14px] leading-[1.3] text-[#777777] underline text-center cursor-pointer bg-transparent border-none p-0 hover:text-[#333] transition"
            >
              {translator.translate(language, "default", "LoginQuestion/forgot_password")}
            </button>
          </div>

          <div className="flex flex-col items-center gap-4 [@media(max-height:760px)]:gap-3 w-full">
            <div className="flex flex-col gap-2 w-full">
              <button
                id={scopeId("wl2_signin_submit")}
                type="button"
                form="variant-b-signin-form"
                onClick={onSubmit}
                disabled={submitLoading}
                className={`w-full h-[55px] [@media(max-height:760px)]:h-12 rounded-[8px] font-['Poppins'] font-medium text-[18px] text-white flex items-center justify-center border-none transition ${
                  submitLoading ? "opacity-70 cursor-wait" : "cursor-pointer hover:opacity-90 active:scale-[0.98]"
                }`}
                style={{ backgroundImage: ORANGE_GRADIENT }}
              >
                {submitLoading
                  ? translator.translate(language, "default", "SubmitButton/loading_text")
                  : translator.translate(language, "authentication", "LoginPage/login_button_text")}
              </button>

              <button
                id={scopeId("wl2_signin_skip")}
                type="button"
                onClick={onSkip}
                className="w-full min-h-[55px] [@media(max-height:760px)]:min-h-12 py-5 [@media(max-height:760px)]:py-[10px] px-8 rounded-[8px] font-['Poppins'] font-medium text-[18px] flex items-center justify-center cursor-pointer bg-transparent hover:bg-[#fff8ee] active:scale-[0.98] transition"
                style={{ border: `1px solid ${ORANGE}`, color: ORANGE }}
              >
                {translator.translate(language, "default", "QuestionnairePage/skip_login")}
              </button>
            </div>

            <p className="font-['Poppins'] font-normal text-[14px] leading-[1.3] text-black text-center m-0">
              {translator.translate(language, "authentication", "LoginPage/new_to_doktorabc")}{" "}
              <Link
                id={scopeId("wl2_signin_register_link")}
                to={`/${country}/register?redirect=${encodeURIComponent(`/${currentPath}`)}`}
                className="font-medium underline cursor-pointer"
                style={{ color: ORANGE }}
              >
                {translator.translate(language, "authentication", "LoginPage/register_now")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </VariantBModalShell>
  );
};

export default SignInModal;
